"use client"
import { useState, useRef, useEffect } from "react"
import { upload } from "@vercel/blob/client"
import { motion, AnimatePresence } from "motion/react"
import { MathCaptcha } from "@/components/ui/MathCaptcha"
import { blobToWavBase64 } from "@/lib/audio-client"
import { normalizeWebsite, reviewAttribution, REVIEW_LINK_REL, REVIEW_LINK_STYLE } from "@/lib/review-display"
import { CheckCircle, AlertCircle, Mic, MicOff, Video, VideoOff, PenLine, Sparkles, Copy, ExternalLink, ImagePlus, X } from "lucide-react"

const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const SURFACE = "rgba(255,255,255,0.05)"
const BORDER = "rgba(255,255,255,0.08)"

type Mode = "written" | "voice" | "video"
type Step = "rate" | "compose" | "done"

// The avatar renders at 44–48px; 640px keeps it sharp on any screen while
// keeping uploads tiny. Re-encoding through a canvas also drops EXIF/GPS data.
const PHOTO_MAX_PX = 640

async function preparePhoto(file: File): Promise<Blob> {
  const src = URL.createObjectURL(file)
  try {
    const img = new Image()
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error("decode")); img.src = src })
    const scale = Math.min(1, PHOTO_MAX_PX / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = document.createElement("canvas")
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height)
    // PNG keeps a logo's transparency; photos go to JPEG.
    const type = file.type === "image/png" || file.type === "image/webp" || file.type === "image/gif" ? "image/png" : "image/jpeg"
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, type, 0.88))
    if (!blob) throw new Error("encode")
    return blob
  } finally {
    URL.revokeObjectURL(src)
  }
}

export function ReviewComposer({ reviewLink, contactRef, clientName, prefillName, prefillEmail, prefillCompany }: { reviewLink: string | null; contactRef?: string | null; clientName?: string | null; prefillName?: string | null; prefillEmail?: string | null; prefillCompany?: string | null }) {
  const [step, setStep] = useState<Step>("rate")
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [mode, setMode] = useState<Mode>("written")
  const [text, setText] = useState("")
  const [name, setName] = useState(prefillName ?? clientName ?? "")
  const [email, setEmail] = useState(prefillEmail ?? "")
  const [company, setCompany] = useState(prefillCompany ?? "")
  const [role, setRole] = useState("")
  const [about, setAbout] = useState("")
  const [website, setWebsite] = useState("")
  const websiteUrl = normalizeWebsite(website)
  // Owner photo or company logo — shown beside the review like the curated ones.
  const [photo, setPhoto] = useState<{ blob: Blob; preview: string } | null>(null)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [session, setSession] = useState<string | null>(null)
  // Set when the server rejects the personalized link (stale contact) — forces
  // the captcha to appear even though contactRef is present.
  const [captchaDemanded, setCaptchaDemanded] = useState(false)
  const [captchaBroken, setCaptchaBroken] = useState(false)
  const [recState, setRecState] = useState<"idle" | "recording" | "transcribing">("idle")
  const [submitState, setSubmitState] = useState<"idle" | "sending" | "error">("idle")
  const [copied, setCopied] = useState(false)
  const [fbCopied, setFbCopied] = useState(false)
  // Blank-page helper: tappable guiding questions (they prompt the reviewer's
  // OWN words — never generated text; see review-page compliance rulings).
  const [guide, setGuide] = useState<string | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const mediaRef = useRef<{ recs: MediaRecorder[]; stream: MediaStream; timer: ReturnType<typeof setTimeout> } | null>(null)
  const videoBlobRef = useRef<Blob | null>(null)
  // Background video upload — kicked off the moment recording stops and fully
  // invisible to the client: submit never waits on it; if it's still running
  // at submit time, the video attaches itself afterwards via
  // /api/review-attach-video.
  const uploadRef = useRef<{ url: string | null; promise: Promise<string | null> } | null>(null)
  const liveVideoRef = useRef<HTMLVideoElement>(null)

  const happy = rating >= 4
  // Personalized links (?c=) skip the captcha — the server verifies the
  // contact against GHL instead, so captcha-hostile networks still work.
  const unlocked = !!captchaToken || !!session || (!!contactRef && !captchaDemanded)

  useEffect(() => () => {
    mediaRef.current?.stream.getTracks().forEach((t) => t.stop())
    clearTimeout(mediaRef.current?.timer)
    if (videoUrl) URL.revokeObjectURL(videoUrl)
    if (photo) URL.revokeObjectURL(photo.preview)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function pickPhoto(file: File | undefined) {
    if (!file) return
    setNotice(null)
    if (!file.type.startsWith("image/")) {
      setNotice("That file isn't an image — a JPG or PNG works best.")
      return
    }
    try {
      const blob = await preparePhoto(file)
      setPhoto((old) => { if (old) URL.revokeObjectURL(old.preview); return { blob, preview: URL.createObjectURL(blob) } })
    } catch {
      setNotice("Couldn't read that image — try a JPG or PNG.")
    }
  }

  function removePhoto() {
    setPhoto((old) => { if (old) URL.revokeObjectURL(old.preview); return null })
  }

  async function startRecording(withVideo: boolean) {
    if (recState !== "idle") {
      mediaRef.current?.recs.forEach((r) => r.state === "recording" && r.stop())
      return
    }
    setNotice(null)
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setNotice("This browser can't record — the written option works everywhere.")
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        withVideo ? { video: { facingMode: "user", width: { ideal: 1280 } }, audio: true } : { audio: true }
      )
      if (withVideo && liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream
        liveVideoRef.current.play().catch(() => {})
      }
      const recs: MediaRecorder[] = []
      const audioChunks: Blob[] = []
      const videoChunks: Blob[] = []

      // Audio-only recorder always runs — it feeds transcription.
      const audioStream = new MediaStream(stream.getAudioTracks())
      const audioRec = new MediaRecorder(audioStream)
      audioRec.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.push(e.data) }
      recs.push(audioRec)

      let videoRec: MediaRecorder | null = null
      // Resolved when the video recorder has fully stopped — by spec its final
      // dataavailable fires before onstop, so awaiting this guarantees the
      // chunks are complete. Mobile encoders can take noticeably longer than
      // desktop to flush; a fixed delay here silently lost phone videos.
      let videoDone: Promise<void> = Promise.resolve()
      if (withVideo) {
        // Prefer mp4 — it plays natively everywhere (Safari, Drive previews,
        // email); fall back to webm on devices whose recorder can't mux mp4.
        const videoMime = ["video/mp4", "video/webm;codecs=vp9,opus", "video/webm"]
          .find((t) => typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(t))
        videoRec = videoMime ? new MediaRecorder(stream, { mimeType: videoMime }) : new MediaRecorder(stream)
        videoRec.ondataavailable = (e) => { if (e.data.size > 0) videoChunks.push(e.data) }
        const vr = videoRec
        videoDone = new Promise((res) => { vr.onstop = () => res() })
        recs.push(videoRec)
      }

      audioRec.onstop = async () => {
        clearTimeout(mediaRef.current?.timer)
        videoRec?.state === "recording" && videoRec.stop()
        stream.getTracks().forEach((t) => t.stop())
        if (liveVideoRef.current) liveVideoRef.current.srcObject = null
        setRecState("transcribing")
        try {
          if (withVideo) {
            await Promise.race([videoDone, new Promise((r) => setTimeout(r, 5000))])
            if (videoChunks.length) {
              const vBlob = new Blob(videoChunks, { type: videoRec?.mimeType || "video/webm" })
              videoBlobRef.current = vBlob
              setVideoUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(vBlob) })
            } else {
              setNotice("Couldn't capture the video from your camera — your words will still be submitted.")
            }
          }
          const wav = await blobToWavBase64(new Blob(audioChunks, { type: audioRec.mimeType || "audio/webm" }))
          const res = await fetch("/api/transcribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ audio: wav, session: session ?? undefined, captchaToken: captchaToken ?? undefined, contactRef: contactRef ?? undefined }),
          })
          const data = await res.json()
          if (res.status === 403) {
            setSession(null); setCaptchaToken(null); setCaptchaDemanded(true)
            setNotice("Quick human check needed below, then re-record.")
            return
          }
          if (!res.ok || !data.ok) throw new Error("transcribe failed")
          if (data.session) setSession(data.session)
          // Start the video upload NOW, in the background — by submit time
          // it's usually finished and the thank-you screen appears instantly.
          const sess = data.session ?? session
          if (withVideo && videoBlobRef.current && sess) {
            const vBlob = videoBlobRef.current
            const entry: { url: string | null; promise: Promise<string | null> } = { url: null, promise: Promise.resolve(null) }
            entry.promise = (async () => {
              try {
                const ext = vBlob.type.includes("mp4") ? "mp4" : "webm"
                const b = await upload(`review-videos/review.${ext}`, vBlob, {
                  access: "public",
                  handleUploadUrl: "/api/review-video-upload",
                  clientPayload: JSON.stringify({ session: sess }),
                })
                entry.url = b.url
                return b.url
              } catch {
                return null
              }
            })()
            uploadRef.current = entry
          }
          setCaptchaToken(null)
          if (data.text) setText((prev) => `${prev}${prev ? " " : ""}${data.text}`.slice(0, 3000))
          else setNotice("Couldn't make that out — try again a little closer to the mic.")
        } catch {
          setNotice("Transcription hit a snag — try again, or type your review instead.")
        } finally {
          setRecState("idle")
        }
      }

      const timer = setTimeout(() => recs.forEach((r) => r.state === "recording" && r.stop()), 60000)
      mediaRef.current = { recs, stream, timer }
      recs.forEach((r) => r.start())
      setRecState("recording")
    } catch {
      setNotice("Your browser blocked the camera/microphone — allow access for boldpiq.com (icon in the address bar) and try again.")
    }
  }

  const emailOk = !!contactRef || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())

  // Uploads the photo straight to Blob storage. An anonymous reviewer only
  // holds a single-use captcha token at this point, so it is traded for a
  // session pass first — the same pass then authorises the submit.
  async function uploadPhoto(blob: Blob): Promise<{ url: string | null; session: string | null; captchaFailed?: boolean }> {
    let sess = session
    if (!sess && !(contactRef && !captchaDemanded) && captchaToken) {
      const res = await fetch("/api/review-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ captchaToken }),
      })
      if (res.status === 403) return { url: null, session: null, captchaFailed: true }
      sess = (await res.json().catch(() => null))?.session ?? null
      if (sess) { setSession(sess); setCaptchaToken(null) }
    }
    try {
      const b = await upload(`review-photos/photo.${blob.type === "image/png" ? "png" : "jpg"}`, blob, {
        access: "public",
        handleUploadUrl: "/api/review-photo-upload",
        clientPayload: JSON.stringify({ session: sess ?? undefined, contactRef: contactRef ?? undefined }),
      })
      return { url: b.url, session: sess }
    } catch {
      return { url: null, session: sess }
    }
  }

  async function submit() {
    if (!name.trim() || !emailOk || text.trim().length < 5 || submitState === "sending") return
    setSubmitState("sending")
    setNotice(null)
    try {
      let submitSession = session
      let storedPhotoUrl: string | undefined
      if (happy && photo) {
        const up = await uploadPhoto(photo.blob)
        if (up.captchaFailed) {
          setSession(null); setCaptchaToken(null); setCaptchaDemanded(true)
          setNotice("Quick human check needed below, then hit send.")
          setSubmitState("idle")
          return
        }
        if (!up.url) {
          setNotice("Your photo didn't upload — try again, pick a different image, or remove it to send without one.")
          setSubmitState("idle")
          return
        }
        storedPhotoUrl = up.url
        submitSession = up.session
      }
      // Never make the client wait on the video: if the background upload has
      // finished, include the URL now; if it's still running, submit without
      // it and let it attach itself when done (fire-and-forget below).
      let storedVideoUrl: string | undefined
      let pendingUpload: Promise<string | null> | null = null
      if (mode === "video" && videoBlobRef.current && uploadRef.current) {
        if (uploadRef.current.url) storedVideoUrl = uploadRef.current.url
        else pendingUpload = uploadRef.current.promise
      }
      const res = await fetch("/api/review-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim() || undefined,
          role: role.trim() || undefined,
          about: about.trim() || undefined,
          photoUrl: storedPhotoUrl,
          website: websiteUrl ?? undefined,
          text: text.trim(),
          source: mode,
          rating,
          marketingConsent,
          videoUrl: storedVideoUrl,
          contactRef: contactRef ?? undefined,
          session: submitSession ?? undefined,
          // Spent on the session exchange when a photo was uploaded.
          captchaToken: submitSession ? undefined : captchaToken ?? undefined,
        }),
      })
      const data = await res.json()
      if (res.status === 403) {
        setSession(null); setCaptchaToken(null); setCaptchaDemanded(true)
        setNotice("Quick human check needed below, then hit send.")
        setSubmitState("idle")
        return
      }
      if (res.status === 409) {
        if (data.session) setSession(data.session)
        setNotice("Looks like you've already shared a review with us recently — thank you, it's on file! If you'd like to change or add to it, just email us at team@boldpiq.com.")
        setSubmitState("idle")
        return
      }
      if (!res.ok || !data.ok) throw new Error("submit failed")
      if (data.session) setSession(data.session)
      // Late-finishing video: attach it to the just-submitted review silently.
      if (pendingUpload) {
        const attachSession = data.session ?? submitSession
        const attachBody = {
          name: name.trim(), email: email.trim(), rating, marketingConsent,
          text: text.trim(), contactRef: contactRef ?? undefined, session: attachSession ?? undefined,
        }
        pendingUpload.then((url) => {
          if (!url) return
          fetch("/api/review-attach-video", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...attachBody, videoUrl: url }),
          }).catch(() => {})
        })
      }
      setSubmitState("idle")
      setStep("done")
    } catch {
      setSubmitState("error")
    }
  }

  async function copyAndOpenGoogle() {
    try { await navigator.clipboard.writeText(text.trim()) ; setCopied(true) } catch { /* clipboard blocked — they can still type */ }
    if (reviewLink) window.open(reviewLink, "_blank", "noopener")
  }

  // Same one-write-many-posts mechanic for Facebook: their words back on the
  // clipboard, our Page's Reviews tab opens, they paste it as a Recommendation.
  // (Like Google, Facebook has no API for posting on a user's behalf.)
  async function copyAndOpenFacebook() {
    try { await navigator.clipboard.writeText(text.trim()) ; setFbCopied(true) } catch { /* clipboard blocked */ }
    window.open("https://www.facebook.com/boldpiq/reviews", "_blank", "noopener")
  }

  const inputStyle: React.CSSProperties = {
    width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`,
    borderRadius: 12, padding: "13px 16px", color: "#fff", fontSize: 14, fontFamily: "inherit",
  }

  return (
    <div style={{ borderRadius: 24, border: `1px solid rgba(196,84,26,0.45)`, background: `linear-gradient(160deg, rgba(196,84,26,0.10) 0%, ${SURFACE} 55%)`, padding: "clamp(26px, 4vw, 44px)" }}>
      <AnimatePresence mode="wait">
        {/* STEP 1 — RATING */}
        {step === "rate" && (
          <motion.div key="rate" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 10 }}>
              {clientName ? <>So, {clientName} — <span style={{ color: ACCENT }}>how did we do?</span></> : <>Worked with us? <span style={{ color: ACCENT }}>How did we do?</span></>}
            </h2>
            <p style={{ color: MUTED, fontSize: 14, marginBottom: 26 }}>Tap a rating — about 30 seconds if you just talk it. Means the world.</p>
            <div role="radiogroup" aria-label="Rate your experience" style={{ display: "inline-flex", gap: 8 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <motion.button key={i} whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
                  onClick={() => { setRating(i); setGuide(null); setStep("compose") }}
                  onMouseEnter={() => setHoverRating(i)} onMouseLeave={() => setHoverRating(0)}
                  aria-label={`${i} star${i > 1 ? "s" : ""}`}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "clamp(34px, 5vw, 46px)", lineHeight: 1, color: i <= (hoverRating || rating) ? "#FBBF24" : "rgba(255,255,255,0.22)" }}>
                  ★
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* STEP 2 — COMPOSE */}
        {step === "compose" && (
          <motion.div key="compose" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ color: "#FBBF24", fontSize: 18 }}>{"★".repeat(rating)}{"☆".repeat(5 - rating)}</span>
              <button onClick={() => setStep("rate")} style={{ background: "none", border: "none", color: MUTED, fontSize: 12, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>change</button>
            </div>
            <h3 style={{ fontSize: "clamp(20px, 2.4vw, 28px)", fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 8 }}>
              {happy ? "Amazing — tell us about it!" : "We're sorry it wasn't right — tell Monique directly."}
            </h3>
            <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              {happy
                ? "Type it, speak it, or film it — we'll turn your words into text you can post to Google in one tap."
                : "This goes straight to the founder for follow-up — we take it seriously and we'll make it right. Type it, speak it, or film it."}
            </p>

            {/* Mode tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
              {([
                { key: "written" as Mode, label: "Write", icon: <PenLine size={14} /> },
                { key: "voice" as Mode, label: "Voice", icon: <Mic size={14} /> },
                { key: "video" as Mode, label: "Video", icon: <Video size={14} /> },
              ]).map((m) => (
                <button key={m.key} onClick={() => setMode(m.key)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 100,
                    fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer",
                    background: mode === m.key ? ACCENT : "rgba(255,255,255,0.06)",
                    border: mode === m.key ? "none" : `1px solid ${BORDER}`, color: "#fff",
                  }}>
                  {m.icon} {m.label}
                </button>
              ))}
            </div>

            {/* Human check gates recording + submission */}
            {!unlocked && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>Quick human check first:</p>
                <MathCaptcha
                  onVerified={(ok, token) => { setCaptchaToken(ok ? token : null); if (ok) setCaptchaBroken(false) }}
                  onLoadError={() => setCaptchaBroken(true)}
                />
                {captchaBroken && (
                  <p role="alert" style={{ display: "flex", alignItems: "flex-start", gap: 6, color: "#FBBF24", fontSize: 12.5, lineHeight: 1.55, marginTop: 8 }}>
                    <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
                    The verification above couldn&rsquo;t complete on your connection. Try refreshing the page or
                    a different network — or use the review link from our email, which skips this check.
                  </p>
                )}
              </div>
            )}

            {/* Video live preview / playback */}
            {mode === "video" && (
              <div style={{ marginBottom: 14 }}>
                <video ref={liveVideoRef} muted playsInline
                  style={{ width: "100%", maxHeight: 320, borderRadius: 16, background: "#000", display: recState === "recording" ? "block" : "none", objectFit: "cover" }} />
                {videoUrl && recState !== "recording" && (
                  <video src={videoUrl} controls playsInline style={{ width: "100%", maxHeight: 320, borderRadius: 16, background: "#000" }} />
                )}
                <p style={{ fontSize: 11.5, color: MUTED, marginTop: 6 }}>
                  We transcribe your words below and save your video with your review.
                </p>
              </div>
            )}

            {/* Record button for voice/video */}
            {mode !== "written" && (
              <motion.button onClick={() => startRecording(mode === "video")} disabled={!unlocked || recState === "transcribing"}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
                  padding: "14px 24px", borderRadius: 100, marginBottom: 14, fontFamily: "inherit", fontWeight: 700, fontSize: 14,
                  background: recState === "recording" || mode === "video" ? "#dc2626" : "rgba(255,255,255,0.06)",
                  border: recState === "recording" || mode === "video" ? "none" : `1px solid ${BORDER}`, color: "#fff",
                  cursor: recState === "transcribing" ? "wait" : "pointer",
                  opacity: unlocked ? 1 : 0.5,
                }}>
                {recState === "recording" ? (
                  <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.9, repeat: Infinity }} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {mode === "video" ? <VideoOff size={15} /> : <MicOff size={15} />} Recording — tap to stop
                  </motion.span>
                ) : recState === "transcribing" ? (
                  <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={{ display: "flex" }}>
                    <Sparkles size={15} />
                  </motion.span>
                ) : (
                  <>{mode === "video" ? <Video size={15} /> : <Mic size={15} />} {text ? "Record more" : `Record your ${mode} review`}</>
                )}
              </motion.button>
            )}

            {/* Guiding questions — company-specific, answered in their own words */}
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>Not sure where to start? Answer one of these:</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(happy
                  ? [
                      "What did Boldpiq build for you?",
                      "What's changed in your business since it went live?",
                      "What would you tell another owner considering Boldpiq?",
                    ]
                  : [
                      "What did you expect that didn't happen?",
                      "Where exactly did we fall short?",
                      "What would make it right?",
                    ]
                ).map((q) => (
                  <button key={q} onClick={() => setGuide(guide === q ? null : q)}
                    style={{
                      padding: "8px 14px", borderRadius: 100, fontFamily: "inherit", fontSize: 12.5, cursor: "pointer",
                      background: guide === q ? "rgba(196,84,26,0.18)" : "rgba(255,255,255,0.05)",
                      border: guide === q ? `1px solid ${ACCENT}` : `1px solid ${BORDER}`,
                      color: guide === q ? "#fff" : MUTED,
                    }}>
                    {q}
                  </button>
                ))}
              </div>
              {guide && (
                <p style={{ fontSize: 13.5, color: ACCENT, fontWeight: 600, marginTop: 10, marginBottom: 0 }}>
                  {guide} — in your own words:
                </p>
              )}
            </div>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={mode === "written"
                ? (happy
                    ? "In your own words — what we built for you, how the process felt, what's changed since. Honest beats polished."
                    : "In your own words — what happened, and what you expected instead. This goes straight to Monique.")
                : "Your transcribed words will appear here — edit them however you like."}
              rows={5}
              maxLength={3000}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.65, marginBottom: 14 }}
            />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name *" aria-label="Your name" style={inputStyle} />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                placeholder={contactRef ? "Email (optional)" : "Email *"}
                aria-label={contactRef ? "Email (optional)" : "Email (required)"} style={inputStyle} />
              <input value={role} onChange={(e) => setRole(e.target.value)} maxLength={120}
                placeholder="Your role, e.g. Founder" aria-label="Your role (optional)" style={inputStyle} />
              <input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120}
                placeholder="Company name" aria-label="Company name (optional)" style={inputStyle} />
              <input value={about} onChange={(e) => setAbout(e.target.value)} maxLength={120}
                placeholder="What your business does, in a few words" aria-label="What your business does (optional)"
                style={{ ...inputStyle, gridColumn: "1 / -1" }} />
              <div style={{ gridColumn: "1 / -1" }}>
                <input value={website} onChange={(e) => setWebsite(e.target.value)} maxLength={200} inputMode="url" autoComplete="url"
                  placeholder="Company website, e.g. yourbusiness.co.za" aria-label="Company website (optional)" style={inputStyle} />
                {website.trim() && !websiteUrl && (
                  <p style={{ color: "#FBBF24", fontSize: 12, marginTop: 6 }}>That doesn&apos;t look like a web address, so your name won&apos;t be linked.</p>
                )}
              </div>
            </div>

            {/* Photo + preview — exactly how the review is attributed on the homepage */}
            {happy && (() => {
              const { title, subtitle } = reviewAttribution({ name: name.trim().replace(/\s+/g, " ") || "Your name", company: company.trim(), role: role.trim(), about: about.trim() })
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 14, border: `1px dashed ${BORDER}`, background: "rgba(255,255,255,0.02)", marginBottom: 14, flexWrap: "wrap" }}>
                  <label title="Add a photo of you or your logo" style={{ width: 48, height: 48, borderRadius: "50%", overflow: "hidden", flexShrink: 0, position: "relative", outline: `2px solid ${ACCENT}`, outlineOffset: "2px", background: "rgba(196,84,26,0.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    {photo ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={photo.preview} alt="Your photo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <ImagePlus size={18} style={{ color: ACCENT }} />
                    )}
                    <input type="file" accept="image/*" aria-label="Add a photo of you or your company logo (optional)"
                      onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = "" }}
                      style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
                  </label>
                  <div style={{ minWidth: 0, flex: "1 1 180px" }}>
                    <p style={{ fontWeight: 700, fontSize: 14 }}>
                      {websiteUrl ? (
                        <a href={websiteUrl} target="_blank" rel={REVIEW_LINK_REL} style={REVIEW_LINK_STYLE}>{title}</a>
                      ) : title}
                    </p>
                    <p style={{ color: MUTED, fontSize: 12.5, lineHeight: 1.45 }}>{subtitle ?? `${"★".repeat(rating)} — verified client review`}</p>
                  </div>
                  <span style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.5, flex: "1 1 160px" }}>
                    {photo ? (
                      <button onClick={removePhoto} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: MUTED, fontSize: 11.5, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", padding: 0 }}>
                        <X size={12} /> Remove photo
                      </button>
                    ) : (
                      <>Tap the circle to add a photo of you or your logo (optional). This is how your review will appear on our site.</>
                    )}
                  </span>
                </div>
              )
            })()}

            {happy && (
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", marginBottom: 16 }}>
                <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)}
                  style={{ marginTop: 3, flexShrink: 0, accentColor: ACCENT, width: 15, height: 15, cursor: "pointer" }} />
                <span style={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
                  Boldpiq may use my {[
                    "review",
                    mode === "video" && "video",
                    photo && "photo",
                    "full name",
                    company.trim() && "company name",
                    role.trim() && "role",
                    about.trim() && "business description",
                    websiteUrl && "website",
                  ].filter(Boolean).join(", ").replace(/, ([^,]+)$/, " and $1")} in its marketing.
                </span>
              </label>
            )}

            {notice && (
              <p role="alert" style={{ display: "flex", alignItems: "center", gap: 6, color: "#FBBF24", fontSize: 13, marginBottom: 12 }}>
                <AlertCircle size={14} /> {notice}
              </p>
            )}
            {submitState === "error" && (
              <p role="alert" style={{ display: "flex", alignItems: "center", gap: 6, color: "#f43f5e", fontSize: 13, marginBottom: 12 }}>
                <AlertCircle size={14} /> Something went wrong — please try again.
              </p>
            )}

            <motion.button onClick={submit}
              disabled={!unlocked || !name.trim() || !emailOk || text.trim().length < 5 || submitState === "sending"}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{
                width: "100%", padding: "15px 28px", borderRadius: 100, border: "none", fontFamily: "inherit",
                background: ACCENT, color: "#fff", fontWeight: 700, fontSize: 15,
                cursor: submitState === "sending" ? "wait" : "pointer",
                opacity: !unlocked || !name.trim() || !emailOk || text.trim().length < 5 ? 0.5 : 1,
              }}>
              {submitState === "sending" ? "Sending…" : happy ? "Continue →" : "Send to Monique"}
            </motion.button>
          </motion.div>
        )}

        {/* STEP 3 — DONE */}
        {step === "done" && (
          <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center" }}>
            <CheckCircle size={40} style={{ color: ACCENT, marginBottom: 16 }} />
            {happy ? (
              <>
                {/* THE GOOGLE MOMENT — loud, celebratory, unmissable */}
                {reviewLink ? (
                  <motion.div
                    initial={{ scale: 0.96 }} animate={{ scale: 1 }}
                    style={{ padding: "clamp(24px, 4vw, 40px)", borderRadius: 20, border: "1px solid rgba(196,84,26,0.65)", background: "linear-gradient(160deg, rgba(196,84,26,0.22) 0%, rgba(196,84,26,0.06) 70%)", boxShadow: "0 0 60px rgba(196,84,26,0.22)", marginBottom: 24 }}>
                    <p style={{ fontSize: 30, letterSpacing: 4, marginBottom: 10 }} aria-hidden>
                      {"★".repeat(rating)}
                    </p>
                    <h3 style={{ fontSize: "clamp(24px, 3vw, 38px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.05, marginBottom: 12 }}>
                      Your review is written.<br /><span style={{ color: ACCENT }}>Make it official on Google.</span>
                    </h3>
                    <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 14.5, lineHeight: 1.65, maxWidth: 440, margin: "0 auto 22px" }}>
                      One tap: your words are copied, Google opens — paste, pick your stars, post. <strong>Ten seconds</strong>, and it helps this small business more than you know. 🧡
                    </p>
                    <motion.button onClick={copyAndOpenGoogle} whileTap={{ scale: 0.97 }}
                      animate={copied ? {} : { scale: [1, 1.04, 1] }}
                      transition={copied ? {} : { duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "18px 44px", borderRadius: 100, border: "none", background: ACCENT, color: "#fff", fontWeight: 800, fontSize: "clamp(15px, 1.6vw, 18px)", fontFamily: "inherit", cursor: "pointer", boxShadow: "0 8px 30px rgba(196,84,26,0.45)" }}>
                      {copied ? <CheckCircle size={18} /> : <Copy size={18} />} {copied ? "Copied — now paste it on Google" : "Post my review on Google"} <ExternalLink size={16} />
                    </motion.button>
                    {copied && (
                      <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.75)", marginTop: 12 }}>
                        Google opened in a new tab — your review is on your clipboard, just paste ✨
                      </p>
                    )}

                    {/* Facebook — secondary: same words, one more paste */}
                    <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                      <p style={{ fontSize: 12.5, color: "rgba(255,255,255,0.65)", marginBottom: 10 }}>
                        On Facebook too? Same words, one more paste:
                      </p>
                      <motion.button onClick={copyAndOpenFacebook} whileTap={{ scale: 0.97 }}
                        style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 26px", borderRadius: 100, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, fontSize: 13.5, fontFamily: "inherit", cursor: "pointer" }}>
                        {fbCopied ? <CheckCircle size={15} /> : <Copy size={15} />} {fbCopied ? "Copied — paste it as a Recommendation" : "Also recommend us on Facebook"} <ExternalLink size={14} />
                      </motion.button>
                    </div>
                  </motion.div>
                ) : (
                  <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.65, maxWidth: 460, margin: "0 auto 22px" }}>
                    We&rsquo;ve saved your words — thank you! Our Google review link is coming online shortly; we may email you a one-tap link to post it.
                  </p>
                )}

                {/* Referral reward — shown to happy clients post-submission.
                    Deliberately NOT tied to the Google review (incentivized
                    reviews violate Google policy and risk the profile). */}
                <div style={{ marginTop: 28, padding: "clamp(20px, 3vw, 28px)", borderRadius: 18, border: `1px solid ${BORDER}`, background: SURFACE, textAlign: "left" }}>
                  <p style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, fontWeight: 800, marginBottom: 8 }}>
                    🎁 A thank-you that pays you back
                  </p>
                  <p style={{ fontWeight: 800, fontSize: "clamp(16px, 1.8vw, 20px)", letterSpacing: "-0.02em", marginBottom: 8 }}>
                    Refer a business — get a month&rsquo;s base fee on us.
                  </p>
                  <p style={{ color: MUTED, fontSize: 13.5, lineHeight: 1.65 }}>
                    Know another business owner who&rsquo;d love what we built for you? When someone you refer becomes a Boldpiq client, your next month&rsquo;s base fee is free. Just have them mention your name when they get in touch — or reply to any of our emails with their details and we&rsquo;ll take it from there.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: "clamp(22px, 2.6vw, 32px)", fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 10 }}>
                  Thank you — <span style={{ color: ACCENT }}>Monique has it.</span>
                </h3>
                <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.65, maxWidth: 460, margin: "0 auto 18px" }}>
                  Your feedback went straight to the founder. If you shared your email, expect a personal reply within one business day — we mean it when we say we&rsquo;ll make it right.
                </p>
                {reviewLink && (
                  <p style={{ fontSize: 12, color: MUTED }}>
                    Prefer to share your experience publicly? <a href={reviewLink} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT }}>You can always post on Google</a>.
                  </p>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
